import type { PricingPlanKey, PricingSection } from '@/types/api/pricing.types'

// Number of pricing plans visible in monthly view
export const MONTHLY_VISIBLE_PLANS = 3

export const SITE_HEADER_OFFSET_DESKTOP = 100
export const SITE_HEADER_OFFSET_MOBILE = 64
export const STICKY_HEADER_BOTTOM_THRESHOLD = 20

export const STICKY_HEADER_FADE_THRESHOLD = 125
export const STICKY_HEADER_FADE_DISTANCE = 125

export const FEATURE_COMPARISON_ANCHOR_ID = 'feature-comparison'

export const pricingPlanFeatures = [
	// Capacity & usage
	'hostedWords',
	'standardAiMtTranslatedWords',
	'projects',
	'languages',
	'standardSeats',
	'processedWords',
	'proAiTranslatedWords',
	'advancedSeats',
	'integrations',
	'automations',
	'otaCalls',
	// AI features
	'aiContext',
	'scoring',
	'smartRouting',
	'customModelsRAG',
	// Translation management
	'pagedDocumentsMicrosoftPdfHtml',
	'spellingGrammar',
	'glossary',
	'translationMemory',
	'translationHistory',
	'pagedDocumentsAdobeFormats',
	'inContextEditorsWebMobile',
	'screenshots',
	'sharedGlossary',
	'translationMemoryManagement',
	'styleguide',
	'autoSnapshots',
	// Collaboration / Project Management
	'collaborativeWebBasedEditor',
	'tasks',
	'taskLocks',
	'customTranslationStatuses',
	'versioningOfDocuments',
	'chainedTasks',
	'branching',
	// Workflow Automation and Customization Features
	'workflowTriggers',
	'workflowTemplates',
	'customWorkflows',
	// Reporting & Analytics
	'analytics',
	'translatorStatistics',
	'projectActivity',
	'auditLogs',
	'vendorRateProfiles',
	'abilityToDownloadGranularTasksData',
	// Security & Permissions
	'permissionsManagementAccessControl',
	'userGroups',
	'securePasswordConfiguration',
	'twoFa',
	'singleSignOnSaml',
	// Support
	'support24_7',
	'digitalSuccessServices',
	'standardSuccess',
	'prioritySupport',
	'dedicatedCsmManager',
	'regularBusinessReviews',
	'privateCommunicationChannel',
	'solutionsEngineer',
	'premiumSuccess',
	'workflowAudits',
] as const satisfies PricingPlanKey[]

export type PricingPlanFeatureKey = (typeof pricingPlanFeatures)[number]

export const pricingSections = [
	{
		key: 'capacityUsage',
		items: [
			'hostedWords',
			'standardAiMtTranslatedWords',
			'projects',
			'languages',
			'standardSeats',
			'processedWords',
			'proAiTranslatedWords',
			'advancedSeats',
			'integrations',
			'automations',
			'otaCalls',
		],
	},
	{
		key: 'aiFeatures',
		items: ['aiContext', 'scoring', 'smartRouting', 'customModelsRAG'],
	},
	{
		key: 'translationManagement',
		items: [
			'pagedDocumentsMicrosoftPdfHtml',
			'spellingGrammar',
			'glossary',
			'translationMemory',
			'translationHistory',
			'pagedDocumentsAdobeFormats',
			'inContextEditorsWebMobile',
			'screenshots',
			'sharedGlossary',
			'translationMemoryManagement',
			'styleguide',
			'autoSnapshots',
		],
	},
	{
		key: 'collaborationProjectManagement',
		items: [
			'collaborativeWebBasedEditor',
			'tasks',
			'taskLocks',
			'customTranslationStatuses',
			'versioningOfDocuments',
			'chainedTasks',
			'branching',
		],
	},
	{
		key: 'workflowAutomationCustomization',
		items: ['workflowTriggers', 'workflowTemplates', 'customWorkflows'],
	},
	{
		key: 'reportingAnalytics',
		items: [
			'analytics',
			'translatorStatistics',
			'projectActivity',
			'auditLogs',
			'vendorRateProfiles',
			'abilityToDownloadGranularTasksData',
		],
	},
	{
		key: 'securityPermissions',
		items: [
			'permissionsManagementAccessControl',
			'userGroups',
			'securePasswordConfiguration',
			'twoFa',
			'singleSignOnSaml',
		],
	},
	{
		key: 'support',
		items: [
			'support24_7',
			'digitalSuccessServices',
			'standardSuccess',
			'prioritySupport',
			'dedicatedCsmManager',
			'regularBusinessReviews',
			'privateCommunicationChannel',
			'solutionsEngineer',
			'premiumSuccess',
			'workflowAudits',
		],
	},
] as const satisfies PricingSection[]

export const PRICING_CTA_SECTIONS = {
	FEATURED: 'featured',
	OVERVIEW: 'overview',
	FEATURE_COMPARISON: 'feature-comparison',
} as const
